import countries from 'world-countries';
import type { Organization } from '../api/organization/organizationApiTypes';

/**
 * The booking's customer fields an organisation fills in when a booking is
 * attributed to it (PO, 2026-10-12; ADR-0012 amendment 2026-10-12): the
 * booking's company and address ARE the invoice recipient, so picking an
 * organisation brings its legal name and billing address along instead of
 * leaving them to be typed. Only what the organisation has is filled; the
 * admin can still change every field.
 *
 * `country` is the English common name, the value booking forms store
 * (`useCountryOptions`); the organisation stores ISO 3166-1 alpha-2.
 */
export interface OrganizationContactPrefill {
    company: string;
    address?: string;
    zipCode?: string;
    city?: string;
    country?: string;
}

const present = (value: string | null | undefined): string | undefined => {
    const v = value?.trim();
    return v ? v : undefined;
};

const countryNameOf = (code: string | null | undefined): string | undefined => {
    const c = present(code)?.toUpperCase();
    if (!c) return undefined;
    return countries.find((country) => country.cca2 === c)?.name.common;
};

export const organizationContactPrefill = (
    organization: Pick<Organization, 'legalName' | 'billingAddress'>,
): OrganizationContactPrefill => {
    const billing = organization.billingAddress;
    const prefill: OrganizationContactPrefill = { company: organization.legalName.trim() };
    const address = present(billing?.street);
    const zipCode = present(billing?.postalCode);
    const city = present(billing?.city);
    const country = countryNameOf(billing?.country);
    if (address) prefill.address = address;
    if (zipCode) prefill.zipCode = zipCode;
    if (city) prefill.city = city;
    if (country) prefill.country = country;
    return prefill;
};

export type PrefillAddressField = 'address' | 'zipCode' | 'city' | 'country';
const ADDRESS_FIELDS: PrefillAddressField[] = ['address', 'zipCode', 'city', 'country'];

/**
 * The prefill for a SECOND pick: an address field the previous organisation
 * filled and the new one does not have is cleared, so the booking never
 * carries the new organisation's name with the old one's street. A field the
 * operator typed themselves is not in `previouslyFilled` and stays.
 */
export const withClearedLeftovers = (
    prefill: OrganizationContactPrefill,
    previouslyFilled: ReadonlySet<PrefillAddressField>,
): OrganizationContactPrefill => {
    const result = { ...prefill };
    for (const field of ADDRESS_FIELDS) {
        if (result[field] === undefined && previouslyFilled.has(field)) result[field] = '';
    }
    return result;
};

/** The address fields a prefill sets to a value (not cleared). */
export const filledAddressFields = (prefill: OrganizationContactPrefill): Set<PrefillAddressField> =>
    new Set(ADDRESS_FIELDS.filter((field) => Boolean(prefill[field])));

/** Hand each filled field to the form's own setter; absent fields stay as they are. */
export const applyContactPrefill = (
    prefill: OrganizationContactPrefill,
    set: {
        company: (value: string) => void;
        address: (value: string) => void;
        zipCode: (value: string) => void;
        city: (value: string) => void;
        country: (value: string) => void;
    },
): void => {
    set.company(prefill.company);
    if (prefill.address !== undefined) set.address(prefill.address);
    if (prefill.zipCode !== undefined) set.zipCode(prefill.zipCode);
    if (prefill.city !== undefined) set.city(prefill.city);
    if (prefill.country !== undefined) set.country(prefill.country);
};
